import type { ScpData } from "@hobbyshopify/backend-client";

// Grade taxonomy shared by the eBay links grid and the manual-grade picker.
//
// Keys and price fields mirror `gradeLinkList` in @hobbyshopify/scan-core — the
// backend prices a manually-graded scan by looking the key up there, so a key
// that disagrees would show one price in the form and store another. Ordering
// and labels follow the iOS `ManualGrade.grades(in:)`.

export type Grade = { key: string; label: string };

// `priceKey` is the grade whose price a category tab shows (its top tier);
// undefined when scp_data has no aggregate for it (e.g. BGS), matching iOS.
export type GradeCategory = { id: string; title: string; priceKey?: string; grades: Grade[] };

// Maps a grade key to its scp_data price field. Grades absent from this map have
// no SportsCardsPro price point — the same ones `gradeLinkList` leaves without a
// `priceKey`, and the ones iOS `priceValue(for:)` returns nil for.
export const SCP_PRICE_KEY: Record<string, keyof ScpData> = {
  raw: "loose-price",
  psa_10: "manual-only-price",
  psa_9: "graded-price",
  psa_8: "new-price",
  psa_7: "cib-price",
  psa_6: "condition-16-price",
  psa_5: "condition-15-price",
  psa_4: "condition-14-price",
  psa_3: "condition-13-price",
  psa_2: "condition-10-price",
  psa_1: "condition-9-price",
  cgc_10: "condition-17-price",
  sgc_10: "condition-18-price",
  cgc_10_pristine: "condition-19-price",
  bgs_10_black: "condition-20-price",
};

export const RAW_GRADE: Grade = { key: "raw", label: "RAW" };

// PSA/SGC/CGC numeric tiers descend 10 -> 1; BGS shares the same tail.
const descend = (prefix: string, label: string, from: number): Grade[] =>
  Array.from({ length: from }, (_, i) => ({
    key: `${prefix}_${from - i}`,
    label: `${label} ${from - i}`,
  }));

export const GRADE_CATEGORIES: GradeCategory[] = [
  { id: "psa", title: "PSA 10", priceKey: "psa_10", grades: descend("psa", "PSA", 10) },
  {
    id: "bgs",
    title: "BGS 10",
    grades: [
      { key: "bgs_10_black", label: "BGS 10 Black" },
      { key: "bgs_gold_label", label: "BGS Gold Label" },
      { key: "bgs_10", label: "BGS 10" },
      { key: "bgs_9_5", label: "BGS 9.5" },
      ...descend("bgs", "BGS", 9),
    ],
  },
  {
    id: "sgc",
    title: "SGC 10",
    priceKey: "sgc_10",
    grades: [{ key: "sgc_gold_label", label: "SGC Gold Label" }, ...descend("sgc", "SGC", 10)],
  },
  {
    id: "cgc",
    title: "CGC 10",
    priceKey: "cgc_10",
    grades: [
      { key: "cgc_10_perfect", label: "CGC 10 Perfect" },
      { key: "cgc_10_pristine", label: "CGC 10 Pristine" },
      ...descend("cgc", "CGC", 10),
    ],
  },
];

const GRADE_BY_KEY = new Map<string, Grade>(
  [RAW_GRADE, ...GRADE_CATEGORIES.flatMap((c) => c.grades)].map((g) => [g.key, g]),
);

/** Label for a stored grade key, falling back to the key itself if unknown. */
export function gradeLabel(key: string): string {
  return GRADE_BY_KEY.get(key)?.label ?? key;
}

/** scp_data prices are in cents; returns undefined when the grade has no price point. */
export function gradePriceCents(key: string, scpData?: ScpData | null): number | undefined {
  const field = SCP_PRICE_KEY[key];
  const cents = field ? (scpData?.[field] as number | undefined) : undefined;
  return cents != null && cents > 0 ? cents : undefined;
}

/** Formatted price for a grade, or "" when there is none — used for inline hints. */
export function gradePriceText(key: string, scpData?: ScpData | null): string {
  const cents = gradePriceCents(key, scpData);
  return cents != null ? `$${(cents / 100).toFixed(2)}` : "";
}

/** The pipeline writes "PSA 10", "psa10" and "PSA 9.0" for one grade. */
const comparableGrade = (text: string) => text.toLowerCase().replace(/\s+/g, "").replace(/\.0$/, "");

/**
 * The key for a grade as written ("PSA 10", "BGS 9.5", "Raw", a stored key, or
 * "Graded PSA 4 VG-EX"), or null when this taxonomy has none for it — a bare
 * "9.5" names no grader.
 *
 * Not `exactGradeKey`, which is the backend's pricing rule and answers only for
 * a grade with a comp: this names any grade the eBay links know, so a BGS 9.5
 * still gets its own link. A port of the iOS `AdminGradeCatalog.key(forFreeformGrade:)`.
 */
export function gradeKeyFromText(text: string | null | undefined): string | null {
  const wanted = comparableGrade(text ?? "");
  if (!wanted) return null;
  for (const [key, grade] of GRADE_BY_KEY) {
    if (comparableGrade(grade.label) === wanted || comparableGrade(key) === wanted) return key;
  }
  const match = /\b(psa|bgs|sgc|cgc)\b\D{0,3}?(\d{1,2}(?:\.5)?)/i.exec(text ?? "");
  if (!match) return null;
  const key = `${match[1].toLowerCase()}_${match[2].replace(".", "_")}`;
  return GRADE_BY_KEY.has(key) ? key : null;
}

/**
 * The estimate for a free-form grade, computed here in the client.
 *
 * ## Why this is a second copy, and what keeps it honest
 *
 * This mirrors `backend/src/core/primitive/grade-price.ts` — the same exact
 * fields, the same round-*down* fallback, the same refusal to price an
 * unparseable slab at the raw figure. The backend answers for a scan as it was
 * read; this answers for the grade the reviewer has just typed, which no
 * request has been made about yet. Sending one per keystroke to find out what a
 * number already in `scp_data` says would be a round trip to re-read a payload
 * the page is holding.
 *
 * `@hobbyshopify/scan-core` owns the parser and would remove the duplication,
 * but it is a server package — the whole pipeline, OpenAI client included — and
 * pulling it into a browser bundle to reach one regex is the worse trade.
 *
 * If the rules change, they change in both places: the backend's are the ones
 * with tests (`npm run test:grade-price`), and a disagreement shows up as a
 * form that prefills one number and a saved card that carries another.
 */

// Copied from scan-core's card-criteria.ts. The full table matters: a named
// grader implies a slab, which is what separates "BGS" with no number (graded,
// unpriceable) from an empty grade (raw, and priced at the raw figure).
const GRADER_ALIASES: Record<string, string> = {
  BECKETT: "BGS",
  BGS: "BGS",
  BVG: "BVG",
  BCCG: "BCCG",
  PSA: "PSA",
  SGC: "SGC",
  CGC: "CGC",
  CSG: "CSG",
  HGA: "HGA",
  ISA: "ISA",
  GMA: "GMA",
  KSA: "KSA",
  AGS: "AGS",
  TAG: "TAG",
};

const GRADER_PATTERN = /\b(BECKETT|BCCG|BGS|BVG|PSA|SGC|CGC|CSG|HGA|ISA|GMA|KSA|AGS|TAG)/i;
const GRADE_PATTERN = /\d{1,3}(?:\.\d)?/;
const UNGRADED_PATTERN = /\b(raw|ungraded|not\s*graded|none|n\/?a)\b/i;
/** SGC's legacy 100-point scale is the reason this is not capped at 10. */
const MAX_GRADE = 100;

function parseGrade(raw: string, slabbedHint: boolean): { company: string; grade: number; slabbed: boolean } {
  const text = raw.trim();
  if (!text) return { company: "", grade: 0, slabbed: slabbedHint };

  const alias = GRADER_PATTERN.exec(text)?.[1]?.toUpperCase();
  const company = alias ? (GRADER_ALIASES[alias] ?? "") : "";
  // "raw" alongside a grader is noise; the grader wins.
  if (!company && UNGRADED_PATTERN.test(text)) return { company: "", grade: 0, slabbed: slabbedHint };

  const parsed = Number(GRADE_PATTERN.exec(text)?.[0]);
  const grade = Number.isFinite(parsed) && parsed > 0 && parsed <= MAX_GRADE ? Math.round(parsed * 10) / 10 : 0;
  // A bare number does not imply a slab; a named grader always does.
  return { company, grade, slabbed: slabbedHint || company !== "" };
}

/** The grade key describing this grade **exactly**, or null when none does. */
export function exactGradeKey(raw: string, slabbedHint: boolean): string | null {
  const { company, grade, slabbed } = parseGrade(raw, slabbedHint);
  // An ungraded card: the raw price is genuinely its own, not a stand-in.
  if (grade === 0) return slabbed ? null : "raw";
  if (company === "PSA" && Number.isInteger(grade) && grade <= 10) return `psa_${grade}`;
  if (grade === 10 && (company === "SGC" || company === "CGC")) return `${company.toLowerCase()}_10`;
  return null;
}

export type GradeEstimate = {
  priceCents: number | null;
  /** The grade the figure was read from. Null when nothing was priced. */
  gradeKey: string | null;
  /** Set only when that is a **different** grade from this card's. */
  approximatedFrom: string | null;
};

const NO_ESTIMATE: GradeEstimate = { priceCents: null, gradeKey: null, approximatedFrom: null };

export function estimateForGrade(grade: string, slabbed: boolean, scpData?: ScpData | null): GradeEstimate {
  const exact = exactGradeKey(grade, slabbed);
  if (exact) {
    const cents = gradePriceCents(exact, scpData);
    return cents == null ? NO_ESTIMATE : { priceCents: cents, gradeKey: exact, approximatedFrom: null };
  }

  // SCP prices PSA 1–10 and the top SGC/CGC grade; everything else — BGS at any
  // grade, a half grade — reads the PSA comp for the grade rounded down. Down,
  // never up: the figure is then always one a real sale supports.
  const { grade: parsed } = parseGrade(grade, slabbed);
  if (parsed <= 0) return NO_ESTIMATE;

  const nearest = Math.min(10, Math.max(1, Math.floor(parsed)));
  const key = `psa_${nearest}`;
  const cents = gradePriceCents(key, scpData);
  // Only claimed when there is a number to qualify.
  return cents == null ? NO_ESTIMATE : { priceCents: cents, gradeKey: key, approximatedFrom: `PSA ${nearest}` };
}
